/**
 * Transaction Table Component
 *
 * Displays the table of transactions with pagination using DataTable.
 */

import * as React from "react";
import { type SortingState } from "@tanstack/react-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { transactionColumns } from "../columns/transactionColumns";
import { TransactionDetailSheet } from "./TransactionDetailSheet";
import type { HistoricalTransaction } from "@/services/paymentService";

interface TransactionTableProps {
  transactions: HistoricalTransaction[];
  loading: boolean;
  error: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
  nextCursor: string | null;
  previousCursor: string | null;
  onNextPage: () => void;
  onPreviousPage: () => void;
  sortBy: "createdAt" | "status" | "province" | "kabkot" | "_id";
  sortOrder: "asc" | "desc";
  onSortChange: (field: "createdAt" | "status" | "province" | "kabkot" | "_id", order: "asc" | "desc") => void;
}

const sortableFieldMap: Record<string, "createdAt" | "status" | "province" | "kabkot" | "_id"> = {
  createdAt: "createdAt",
  status: "status",
  province: "province",
  kabkot: "kabkot",
  callsign: "_id",
};

export function TransactionTable({
  transactions,
  loading,
  error,
  hasNext,
  hasPrevious,
  onNextPage,
  onPreviousPage,
  sortBy,
  sortOrder,
  onSortChange,
}: TransactionTableProps) {
  const [selectedTransaction, setSelectedTransaction] = React.useState<HistoricalTransaction | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);

  const sorting: SortingState = React.useMemo(
    () => [{ id: sortBy === "_id" ? "callsign" : sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const handleSortingChange = React.useCallback(
    (newSorting: SortingState) => {
      if (newSorting.length === 0) return;
      const { id, desc } = newSorting[0];
      const field = sortableFieldMap[id] ?? "createdAt";
      onSortChange(field, desc ? "desc" : "asc");
    },
    [onSortChange]
  );

  const handleRowClick = React.useCallback((row: HistoricalTransaction) => {
    setSelectedTransaction(row);
    setDetailOpen(true);
  }, []);

  const columns = React.useMemo(() => transactionColumns, []);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <CardTitle>Daftar Transaksi</CardTitle>
              <CardDescription>Data transaksi pembayaran</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={transactions}
            loading={loading}
            error={error}
            emptyMessage="Tidak ada data transaksi"
            manualSorting
            sorting={sorting}
            onSortingChange={handleSortingChange}
            showColumnToggle
            onRowClick={handleRowClick}
            pagination={{
              hasNext,
              hasPrevious,
              onNextPage,
              onPreviousPage,
              loading,
              info: `Menampilkan ${transactions.length} transaksi`,
            }}
          />
        </CardContent>
      </Card>

      <TransactionDetailSheet
        transaction={selectedTransaction}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </>
  );
}
